/**
 * One Generate run as a gallery: the chosen view large, a thumbnail per view below it. A view
 * waiting for review is approved or made again here, one by one or all at once; a settled view
 * is made again from its stored inputs as a new job of the same run.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { GenerationJobView, JsonClient } from "@genz/platform";
import { useServices } from "../app/context.js";
import { Icon } from "../ui/Icon.js";
import { ConfirmPopover } from "../ui/ConfirmPopover.js";
import { ErrorNotice } from "../ui/ErrorNotice.js";
import { hasImage, inPassTwo, isReady, jobBadge, jobLabel } from "./candidates.js";
import { errorDetail } from "./errorCode.js";
import { refusalText } from "./jobActions.js";
import { RunThumb } from "./RunRow.js";
import { latestPerView, mockupOnlyRun, phoneName, runCounts, runCountsText, viewLabel, type Run } from "./runs.js";
import { useStableUrl } from "./stableUrl.js";
import { useGuideSafeUrl } from "./whiteGuide.js";

export interface RunGalleryProps {
  run: Run;
  admin: boolean;
  onBack: () => void;
  /** A view's job moved on, or a Regenerate added a job to the run. */
  onJob: (job: GenerationJobView) => void;
  /** A batch of requests ended: the poll is asked once, not once per view. */
  onSettled?: () => void;
  /** Focus Back to list on open: a person opened the run, it did not open by itself. */
  autoFocus?: boolean;
  /** The chosen view's own status line and actions, under the preview. */
  viewRow?: (job: GenerationJobView) => ReactNode;
  /** A preview's signed link did not load (they expire), or did. */
  onImageError?: (jobId: string) => void;
  onImageLoad?: (jobId: string) => void;
}

type Shown = "clear" | "guide";
interface Failure { detail: string; reference: string }

const inReview = (job: GenerationJobView) => job.status === "clear_review";

/** The cloud makes a settled job again from its stored inputs; a device job's inputs are not there. */
const SETTLED = new Set<GenerationJobView["status"]>(["completed", "needs_review", "approved", "failed", "cancelled"]);
const cloudRegenerable = (job: GenerationJobView) => SETTLED.has(job.status) && job.executor !== "device";

/** The clear image while it waits for review or under pass 2; then the print guide, or the clear image on request. */
function previewOf(job: GenerationJobView, shown: Shown): { url: string | null | undefined; kind: Shown | "mockup" } | null {
  if (inReview(job) || inPassTwo(job)) return job.clearUrl ? { url: job.clearUrl, kind: "clear" } : null;
  if (!isReady(job)) return null;
  if (job.mockupOnly) return { url: job.resultUrl, kind: "mockup" };
  return shown === "clear" && job.clearUrl ? { url: job.clearUrl, kind: "clear" } : { url: job.resultUrl, kind: "guide" };
}

const CAPTION = { clear: "Clear image", guide: "Print guide", mockup: "Mock-up" } as const;

/** The case and phone photos a view in review was made from, asked for once per job. */
function useReferences(client: JsonClient, job: GenerationJobView): GenerationJobView | undefined {
  const [byJob, setByJob] = useState<Record<string, GenerationJobView>>({});
  const wanted = inReview(job) && !byJob[job.id];
  useEffect(() => {
    if (!wanted) return;
    let live = true;
    client.getGenerationJob(job.id, { inputs: true })
      .then((full) => { if (live) setByJob((prev) => ({ ...prev, [job.id]: full })); }, () => undefined);
    return () => { live = false; };
  }, [client, job.id, wanted]);
  return inReview(job) ? byJob[job.id] : undefined;
}

export function RunGallery({
  run, admin, onBack, onJob, onSettled, autoFocus, viewRow, onImageError, onImageLoad,
}: RunGalleryProps) {
  const { client } = useServices();
  const views = latestPerView(run);
  const [chosenView, setChosenView] = useState(() => (views.find(hasImage) ?? views[0]).view);
  const chosen = views.find((j) => j.view === chosenView) ?? views[0];
  const [shown, setShown] = useState<Shown>("guide");
  const [busy, setBusy] = useState(false);
  const [failures, setFailures] = useState<Failure[]>([]);
  const mockupOnly = mockupOnlyRun(run);
  const toReview = views.filter(inReview);
  const back = useRef<HTMLButtonElement>(null);
  // Only on open: a gallery that opens by itself must not take the caret from the form.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (autoFocus) back.current?.focus(); }, []);

  const preview = previewOf(chosen, shown);
  const [previewUrl, dropPreview] = useStableUrl(
    `${chosen.id}:${preview?.kind}:${preview?.kind === "clear" ? chosen.clearRef ?? "" : chosen.finishedAt ?? ""}`,
    preview?.url,
  );
  const guide = useGuideSafeUrl(preview?.kind === "guide" ? previewUrl : null);
  const src = preview?.kind === "guide" ? guide : previewUrl;

  const references = useReferences(client, chosen);
  const { name, brand, model } = run.details;
  const phone = phoneName(brand, model) || "Phone not named";

  const choose = (view: string) => {
    setChosenView(view);
    setShown("guide");
  };

  /** One request at a time; each refusal is kept for the person with its job's reference. */
  const working = async (requests: () => Promise<void>) => {
    setBusy(true);
    setFailures([]);
    try {
      await requests();
    } finally {
      setBusy(false);
      onSettled?.();
    }
  };
  const refused = (failure: Failure) => setFailures((prev) => [...prev, failure]);

  /** A refused decision leaves the view for the person, reloaded so the next one names the image now there. */
  const decide = async (job: GenerationJobView, decision: "approve" | "regenerate") => {
    try {
      onJob(await client.reviewClearImage(job.id, decision, job.clearRef ?? ""));
    } catch (e) {
      refused({ detail: errorDetail(e), reference: job.id });
      await client.getGenerationJob(job.id).then(onJob, () => undefined);
    }
  };
  const approveAll = () => working(async () => {
    for (const job of toReview) await decide(job, "approve");
  });
  const regenerate = (job: GenerationJobView) => working(async () => {
    try {
      onJob(await client.regenerateGenerationJob(job.id));
    } catch (e) {
      refused({ detail: refusalText(e, { admin, reference: job.id }), reference: job.id });
    }
  });

  const label = viewLabel(chosen.view);
  return (
    <section className="generate-panel__gallery" aria-label={`Generation: ${phone}`} data-testid="run-gallery">
      <header className="generate-panel__gallery-head">
        <button ref={back} type="button" className="btn btn--sm" onClick={onBack} data-testid="run-gallery-back">
          <Icon name="arrow-left" size={16} />
          Back to list
        </button>
        <span className="generate-panel__run-text">
          <span className="generate-panel__run-name">{phone}</span>
          {name?.trim() && <span className="caption muted">{name.trim()}</span>}
          <span className="caption" data-testid="run-gallery-counts">{runCountsText(runCounts(run))}</span>
        </span>
      </header>

      {failures.map((f) => (
        <p key={f.reference} className="banner banner--danger" role="alert" data-testid="run-gallery-error">
          <ErrorNotice detail={f.detail} reference={f.reference} />
        </p>
      ))}

      <figure className="generate-panel__gallery-stage">
        {preview && src ? (
          <img className="generate-panel__gallery-img" src={src} alt={`${CAPTION[preview.kind]}: ${label}`}
            onError={() => { dropPreview(); onImageError?.(chosen.id); }} onLoad={() => onImageLoad?.(chosen.id)}
            data-testid="run-gallery-preview" />
        ) : (
          <div className="generate-panel__gallery-img is-empty" data-testid="run-gallery-preview-empty">
            <span className="caption">{jobLabel(chosen)}</span>
          </div>
        )}
        <figcaption className="generate-panel__gallery-caption">
          <span className="generate-panel__run-name">{label}</span>
          {preview && <span className="caption muted">{CAPTION[preview.kind]}</span>}
        </figcaption>
      </figure>

      {references && (
        <div className="generate-panel__gallery-refs">
          {references.inputUrl && (
            <figure className="generate-review__figure">
              <img className="generate-review__img" src={references.inputUrl} alt="Case reference" data-testid="run-gallery-case" />
              <figcaption className="caption">Case reference</figcaption>
            </figure>
          )}
          {references.productUrl && (
            <figure className="generate-review__figure">
              <img className="generate-review__img" src={references.productUrl} alt="Product reference" data-testid="run-gallery-product" />
              <figcaption className="caption">Product reference</figcaption>
            </figure>
          )}
        </div>
      )}

      <div className="generate-panel__gallery-actions">
        {isReady(chosen) && !chosen.mockupOnly && chosen.clearUrl && (
          <div className="seg" role="group" aria-label="Image shown">
            {(["guide", "clear"] as const).map((kind) => (
              <button key={kind} type="button" className={`seg__btn${shown === kind ? " seg__btn--on" : ""}`}
                aria-pressed={shown === kind} onClick={() => setShown(kind)} data-testid={`run-gallery-show-${kind}`}>
                {CAPTION[kind]}
              </button>
            ))}
          </div>
        )}
        {inReview(chosen) && !mockupOnly && (
          <>
            <span className="caption">Regenerate makes a new clear image and uses credits.</span>
            <button type="button" className="btn" onClick={() => void working(() => decide(chosen, "regenerate"))}
              disabled={busy} data-testid="run-gallery-regenerate">
              Regenerate
            </button>
            <button type="button" className="btn" onClick={() => void working(() => decide(chosen, "approve"))}
              disabled={busy} data-testid="run-gallery-approve">
              Approve
            </button>
          </>
        )}
        {cloudRegenerable(chosen) && (
          <ConfirmPopover message={`Regenerate ${label}? It makes a new image and uses credits. This image is kept.`}
            confirmLabel="Regenerate" onConfirm={() => void regenerate(chosen)}>
            {(toggle) => (
              <button type="button" className="btn" onClick={toggle} disabled={busy} data-testid="run-gallery-regenerate">
                Regenerate
              </button>
            )}
          </ConfirmPopover>
        )}
      </div>

      {viewRow?.(chosen)}

      <ul className="generate-panel__gallery-thumbs" aria-label="Views">
        {views.map((job) => (
          <li key={job.id}>
            <button type="button" className="generate-panel__gallery-thumb" aria-pressed={job.view === chosen.view}
              aria-label={`${viewLabel(job.view)} · ${jobLabel(job)}`}
              onClick={() => choose(job.view)} data-testid={`run-gallery-thumb-${job.view}`}>
              <RunThumb job={job} testId={`run-gallery-img-${job.view}`} decorative
                onImageError={onImageError} onImageLoad={onImageLoad} />
              <span className="caption">{viewLabel(job.view)}</span>
              <span className={`badge badge--plain badge--${jobBadge(job).variant}`} data-testid={`run-gallery-badge-${job.view}`}>
                {jobLabel(job)}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {!mockupOnly && toReview.length > 0 && (
        <footer className="generate-panel__gallery-foot">
          <button type="button" className="btn" onClick={() => void approveAll()} disabled={busy}
            data-testid="run-gallery-approve-all">
            Approve all ({toReview.length})
          </button>
        </footer>
      )}
    </section>
  );
}
